import { Modal } from 'antd';
import { getAdminProviderListingOverdue } from 'api/operation/billing';
import { AdminProviderBillingOverdueVo } from 'types/financials';

// Keep amount formatting consistent with ListingFeeBanner: "$1,500".
const formatAmount = (cents: number) => `$${(cents / 100).toLocaleString()}`;

const buildContent = (data: AdminProviderBillingOverdueVo) => {
    const lead = data.leadGenFailedCents || 0;
    const pro = data.proListingFailedCents || 0;

    // Multiple fees — both lead-gen and pro-listing are outstanding.
    if (data.bannerType === 'BOTH_OFFLINE') {
        return `This provider has unpaid balances totaling ${formatAmount(
            lead + pro,
        )}: lead-generation fee ${formatAmount(lead)} and pro-listing fee ${formatAmount(
            pro,
        )}. Re-listing now will restore visibility before the balance is cleared.`;
    }

    // Single fee — lead-gen or pro-listing.
    const isLeadGen = data.bannerType === 'LEAD_GEN_OFFLINE';
    const feeType = isLeadGen ? 'lead-generation fee' : 'pro-listing fee';
    const amount = isLeadGen ? lead : pro;
    return `This provider has an unpaid ${feeType} of ${formatAmount(
        amount,
    )}. Re-listing now will restore visibility before the balance is cleared.`;
};

// Ops Re-list guard: look up the provider's listing-fee status; if `bannerType` is anything other
// than NONE, confirm before re-listing. Otherwise (or if the lookup fails) run `onProceed` directly
// so Ops is never blocked.
export const confirmRelistIfOverdue = async (providerId: number, onProceed: () => void | Promise<void>) => {
    try {
        const res = await getAdminProviderListingOverdue(providerId);
        const data: AdminProviderBillingOverdueVo | undefined = res?.error ? undefined : res?.data?.data;

        if (!data || data.bannerType === 'NONE') {
            await onProceed();
            return;
        }

        // Modal needed — open it and resolve immediately so callers can stop their loading state;
        // the actual re-list runs when the user confirms.
        Modal.confirm({
            title: 'This provider has an outstanding balance',
            content: buildContent(data),
            okText: 'Re-list',
            cancelText: 'Cancel',
            onOk: onProceed,
            width: 512,
        });
    } catch (e) {
        console.error(e);
        await onProceed();
    }
};
